import { useEffect, useState } from 'react';
import 'react-popper-tooltip/dist/styles.css';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { usePopperTooltip } from 'react-popper-tooltip';

import appleIcon from '../../../assets/platforms/appleIcon.svg';
import facebookIcon from '../../../assets/platforms/facebookIcon.svg';
import googleIcon from '../../../assets/platforms/googleIcon.svg';
import Button from '../../../components/buttons/Button';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { InfoIcon } from '../../../components/SVGIcons/InfoIcon';
import { useMarketingConsents } from '../../../hooks/useMarketingConsents';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';
import { checkIfAppleDevice } from '../../../utils/helpers';
import { isGoogleSignInBlocked as checkGoogleSignInBlocked } from '../../../utils/isInAppBrowser';
import { useHandleAuth } from '../useHandleAuth';
import { useHandleSignUpWizard } from '../wizards/useHandleSIgnUpWizard';

import InAppBlockedTooltip from './InAppBlockedTooltip/InAppBlockedTooltip';
import styles from './ThirdPartyAuth.module.scss';

type ThirdPartyAuthProvider = 'apple' | 'google' | 'facebook';

export const ThirdPartyAuth = observer(
  ({
    action,
    excludedProviders = [],
    onLoginAttempt,
    className,
    googleIconClassName,
    hideLoader = false,
  }: {
    action: 'Sign Up' | 'Log in';
    excludedProviders?: ThirdPartyAuthProvider[];
    onLoginAttempt?: () => boolean;
    className?: string;
    googleIconClassName?: string;
    hideLoader?: boolean;
  }) => {
    const { authStore, marketingConsentStore } = useStores();
    const { t } = useTranslation();

    const { isSigningUp } = authStore;
    const isDirectLanding = authStore.isDirectLanding();

    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
      trigger: 'click',
      placement: 'bottom-end',
    });

    const [error, setError] = useState<string>();

    const { handleAuth } = useHandleAuth(setError);
    const { handleSignUpWizard } = useHandleSignUpWizard();

    const isGoogleSignInBlocked = checkGoogleSignInBlocked();

    const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;
    const { areAllOptionalConsentsChecked } = useMarketingConsents({
      initialMarketingConsents: signUpMarketingConsents,
      fetchingMarketingConsents: fetchingSignUpMarketingConsent,
      localStorageKey: 'signup-marketing-consents',
    });

    const handleSocialSignIn = (provider: 'apple' | 'google' | 'facebook') => () => {
      if (isGoogleSignInBlocked && provider === 'google') {
        return;
      }

      const actionFormatted = action === 'Sign Up' ? 'SIGN_UP' : 'LOG_IN';

      if (actionFormatted === 'SIGN_UP' && isDirectLanding && typeof onLoginAttempt === 'function') {
        const isValid = onLoginAttempt();

        if (!isValid) {
          return;
        }
      }

      if (actionFormatted === 'SIGN_UP') {
        handleSignUpWizard({ provider, action: 'sign_up', areAllOptionalConsentsChecked });
      } else {
        void analytics.sendEvent('login_account', {
          event_category: 'sign_up',
          event_action: 'click',
          event_type: 'login_platform',
          event_value: provider,
          button_text: 'log in',
        });
        void handleAuth({ provider, action: 'log_in' });
      }
    };

    useEffect(() => {
      authStore.setPending('thirdPartyAuth', false);
    }, [authStore]);

    return (
      <div className={classNames(styles.thirdPartyAuth, className)}>
        {!hideLoader && <DialogLoader isProcessing={isSigningUp} />}

        {checkIfAppleDevice() && !excludedProviders.includes('apple') && (
          <Button
            btnType="light"
            className={styles.thirdPartyAuthButton}
            disabled={isSigningUp}
            iconLeft={<img alt="apple-icon" className={styles.iconImg} src={appleIcon} />}
            onClick={handleSocialSignIn('apple')}
          >
            {action === 'Sign Up' ? t('account.signUpApple') : t('account.signInApple')}
          </Button>
        )}

        {!excludedProviders.includes('google') && (
          <>
            <Button
              ref={setTriggerRef}
              btnType="light"
              className={classNames(styles.thirdPartyAuthButton, isGoogleSignInBlocked && styles.withRightIcon)}
              classNameLeftIcon={googleIconClassName}
              disabled={isSigningUp}
              iconLeft={<img alt="google-icon" className={styles.iconImg} src={googleIcon} />}
              iconRight={isGoogleSignInBlocked ? <InfoIcon color="red" /> : undefined}
              onClick={handleSocialSignIn('google')}
            >
              {action === 'Sign Up' ? t('account.signUpGoogle') : t('auth.logInWithGoogle')}
            </Button>
            {visible && isGoogleSignInBlocked && (
              <InAppBlockedTooltip
                getArrowProps={getArrowProps}
                getTooltipProps={getTooltipProps}
                setTooltipRef={setTooltipRef}
              />
            )}
          </>
        )}

        {!excludedProviders.includes('facebook') && (
          <Button
            btnType="blue"
            className={styles.thirdPartyAuthButton}
            disabled={isSigningUp}
            iconLeft={<img alt="facebook-icon" className={styles.iconImg} src={facebookIcon} />}
            onClick={handleSocialSignIn('facebook')}
          >
            {action === 'Sign Up' ? t('account.signUpFacebook') : t('account.signInFacebook')}
          </Button>
        )}

        <ErrorMessage noSpaceWhenEmpty>{error}</ErrorMessage>
      </div>
    );
  },
);
